import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	ChevronUp,
	Eye,
	Filter as FilterIcon,
	RefreshCw,
	Search
} from 'lucide-react';
import type { OrderToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pill } from '~/components/pill';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { formatCurrency } from '~/lib/dashboard/format';
import { formatDateTime, formatDate } from '~/lib/format-date';
import { useFilteredOrdersList } from '~/hooks/use-filtered-orders';
import { useOrderStatuses } from '~/hooks/use-skroutz-orders';
import { useShippingTypesList } from '~/hooks/use-shipping-types';

const PAGE_SIZES = [10, 20, 50, 100];

/** Filtered (sales-person / B2B) orders — read-only, server-paged grid mirroring the legacy /sp-orders. */
export function FilteredOrdersGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [search, setSearch] = useState('');
	const [searchInput, setSearchInput] = useState('');
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

	useEffect(() => {
		const h = setTimeout(() => {
			setSearch(searchInput);
			setPageIndex(0);
		}, 350);
		return () => clearTimeout(h);
	}, [searchInput]);

	const { data, isLoading, isError, isFetching, refetch } = useFilteredOrdersList({
		skip: pageIndex * pageSize,
		take: pageSize,
		search,
		sortDir
	});
	const { data: statuses } = useOrderStatuses();
	const { data: shippingTypes } = useShippingTypesList();

	const statusName = useMemo(() => {
		const map = new Map<number, string>();
		for (const s of statuses ?? []) if (s.id != null) map.set(s.id, s.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [statuses]);

	const shippingName = useMemo(() => {
		const map = new Map<number, string>();
		for (const s of shippingTypes ?? []) if (s.id != null) map.set(s.id, s.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [shippingTypes]);

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const from = total === 0 ? 0 : pageIndex * pageSize + 1;
	const to = Math.min((pageIndex + 1) * pageSize, total);

	if (isError) {
		return (
			<div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface py-16 text-center">
				<FilterIcon size={40} className="text-danger-fg" strokeWidth={1.25} />
				<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
				<Button variant="outline" onClick={() => void refetch()}>
					{t('common.retry')}
				</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('orders.filtered.title')}</h1>
			</div>

			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						placeholder={t('orders.filtered.searchPlaceholder')}
						className="pl-8"
						aria-label={t('common.search')}
					/>
				</div>
			</div>

			<div className="flex items-center justify-between gap-4 rounded-t-md border border-b-0 border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
				<span>
					{t('system.grid.showing')}{' '}
					<strong className="font-medium tabular-nums text-foreground">
						{from}–{to}
					</strong>{' '}
					{t('system.grid.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
				</span>
				<button
					type="button"
					onClick={() => void refetch()}
					title={t('common.refresh')}
					className="rounded-sm p-1.5 text-foreground-muted hover:bg-muted hover:text-foreground">
					<RefreshCw size={13} className={isFetching ? 'animate-spin' : undefined} />
				</button>
			</div>

			<div className="overflow-x-auto rounded-b-md border border-border bg-surface">
				<table className="w-full border-collapse text-left text-sm" style={{ minWidth: 1100 }}>
					<thead>
						<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
							<th className="px-3 py-2">{toGreekCaps(t('orders.filtered.columns.code'))}</th>
							<th className="px-3 py-2">
								<button
									type="button"
									onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
									className="inline-flex items-center gap-1 hover:text-foreground">
									{toGreekCaps(t('orders.filtered.columns.purchaseDate'))}
									{sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
								</button>
							</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.filtered.columns.deliveryDate'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.filtered.columns.customer'))}</th>
							<th className="px-3 py-2 text-right">{toGreekCaps(t('orders.filtered.columns.quantity'))}</th>
							<th className="px-3 py-2 text-right">{toGreekCaps(t('orders.filtered.columns.total'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.filtered.columns.shipping'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.filtered.columns.status'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.filtered.columns.salesPersons'))}</th>
							<th className="px-3 py-2" />
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={10} className="p-4">
									<Skeleton className="h-72 w-full rounded-md" />
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={10}>
									<div className="flex flex-col items-center gap-2 py-16 text-center">
										<FilterIcon size={36} className="text-foreground-disabled" strokeWidth={1.25} />
										<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
									</div>
								</td>
							</tr>
						) : (
							rows.map((r: OrderToken) => (
								<tr key={r.id ?? r.uid} className="border-b border-border-subtle last:border-b-0 hover:bg-elevated">
									<td className="px-3 py-2">
										<button
											type="button"
											onClick={() => r.id && navigate(`/orders/${r.id}`)}
											className="font-mono text-xs font-medium text-foreground hover:underline">
											{r.uid || '—'}
										</button>
									</td>
									<td className="px-3 py-2 text-foreground-muted">{formatDateTime(r.pdt)}</td>
									<td className="px-3 py-2 text-foreground-muted">{formatDate(r.delivery_date)}</td>
									<td className="px-3 py-2 text-foreground">{r.cnm || '—'}</td>
									<td className="px-3 py-2 text-right tabular-nums text-foreground-muted">{r.total_quantity ?? 0}</td>
									<td className="px-3 py-2 text-right tabular-nums text-foreground">{formatCurrency(r.gamt ?? 0)}</td>
									<td className="px-3 py-2 text-foreground-muted">{shippingName(r.stid)}</td>
									<td className="px-3 py-2">
										<Pill variant="neutral">{statusName(r.st)}</Pill>
									</td>
									<td className="px-3 py-2">
										<span className="flex flex-wrap gap-1">
											{(r.sales_persons ? r.sales_persons.split(', ') : []).map((p, i) => (
												<span key={i} className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-foreground-muted">
													{p}
												</span>
											))}
										</span>
									</td>
									<td className="px-3 py-2">
										<span className="flex items-center justify-end">
											<Button
												variant="ghost"
												size="icon"
												aria-label={t('orders.filtered.details')}
												title={t('orders.filtered.details')}
												onClick={() => r.id && navigate(`/orders/${r.id}`)}>
												<Eye size={15} />
											</Button>
										</span>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className="mt-1 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<Select
						value={String(pageSize)}
						onValueChange={v => {
							setPageSize(Number(v));
							setPageIndex(0);
						}}>
						<SelectTrigger className="h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{PAGE_SIZES.map(size => (
								<SelectItem key={size} value={String(size)}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex === 0}
						onClick={() => setPageIndex(0)}
						aria-label={t('system.grid.first')}>
						<ChevronsLeft size={15} />
					</Button>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex === 0}
						onClick={() => setPageIndex(p => Math.max(0, p - 1))}
						aria-label={t('system.grid.prev')}>
						<ChevronLeft size={15} />
					</Button>
					<span className="px-2 tabular-nums">
						{pageIndex + 1} / {pageCount}
					</span>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex >= pageCount - 1}
						onClick={() => setPageIndex(p => Math.min(pageCount - 1, p + 1))}
						aria-label={t('system.grid.next')}>
						<ChevronRight size={15} />
					</Button>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex >= pageCount - 1}
						onClick={() => setPageIndex(pageCount - 1)}
						aria-label={t('system.grid.last')}>
						<ChevronsRight size={15} />
					</Button>
				</div>
			</div>
		</div>
	);
}
